import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Form, Input, Button, Select, Switch, message, Spin } from 'antd';
import { AppDispatch, RootState } from '../../../redux/store/store';
import { updateUsuario, UsuarioDto } from '../../../redux/actions/usuarios/usuarios';
import { fetchSucursales } from '../../../redux/actions/sucursales/sucursales';
import { fetchTiposUsuarios } from '../../../redux/actions/tipoUsuarios/TipoUsuarios';
import { fetchEmpresas } from '../../../redux/actions/empresas/Empresas';
import EmpresasSelect from '../../select/EmpresasSelect';

const { Option } = Select;

interface EditarUsuarioModalProps {
  visible: boolean;
  usuarioId: number | null;
  onClose: (refresh?: boolean) => void;
}

const EditarUsuarioModal: React.FC<EditarUsuarioModalProps> = ({
  visible,
  usuarioId,
  onClose
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [selectedEmpresaId, setSelectedEmpresaId] = useState<string | undefined>(undefined);
  const [sucursalesIds, setSucursalesIds] = useState<number[]>([]);
  const [contrasenaActual, setContrasenaActual] = useState<string | undefined>(undefined);
  const { usuarios } = useSelector((state: RootState) => state.usuarios);
  const { sucursales } = useSelector((state: RootState) => state.sucursales);
  const { tiposUsuarios } = useSelector((state: RootState) => state.tiposUsuarios || { tiposUsuarios: [] });
  const usuarioActual = usuarios.find(usuario => usuario.id === usuarioId);

  useEffect(() => {
    const loadData = async () => {
      if (visible) {
        setLoadingData(true);
        try {
          await Promise.all([
            dispatch(fetchTiposUsuarios(1, 10)),
            dispatch(fetchEmpresas(1, 10))
          ]);
        } catch (error) {
          console.error('Error al cargar datos:', error);
          message.error('Error al cargar la información necesaria');
        } finally {
          setLoadingData(false);
        }
      }
    };

    loadData();
  }, [dispatch, visible]);

  useEffect(() => {
    if (usuarioActual) {
      setContrasenaActual(undefined);
      setSelectedEmpresaId(usuarioActual.empresaId?.toString());

      const sucursalIds = usuarioActual.sucursales?.map(s => s.id) || [];
      setSucursalesIds(sucursalIds);

      form.setFieldsValue({
        nombre: usuarioActual.personas?.nombre,
        apellidos: usuarioActual.personas?.apellidos,
        email: usuarioActual.email,
        telefono: usuarioActual.telefono,
        pais: usuarioActual.pais,
        imagen_perfil: usuarioActual.imagen_perfil,
        tipoUsuarioId: usuarioActual.tipoUsuarioId,
        empresaId: usuarioActual.empresaId,
        empresaNombre: usuarioActual.empresa?.nombre,
        sucursalesIds: sucursalIds,
        estado: usuarioActual.estado,
        timezone: usuarioActual.timezone || 'America/Lima',
        cambiar_contrasena: false
      });

      if (usuarioActual.empresaId) {
        dispatch(fetchSucursales(1, 10, undefined, usuarioActual.empresaId));
      }
    }
  }, [usuarioActual, form, dispatch]);

  useEffect(() => {
    if (selectedEmpresaId) {
      const fetchData = async () => {
        try {
          await dispatch(fetchSucursales(1, 10, undefined, Number(selectedEmpresaId)));
        } catch (error) {
          console.error('Error al cargar sucursales:', error);
        }
      };
      fetchData();
    }
  }, [dispatch, selectedEmpresaId]);

  const handleEmpresaChange = (value: string, empresa: any) => {
    setSelectedEmpresaId(value);
    if (empresa) {
      form.setFieldsValue({
        empresaId: value,
        empresaNombre: empresa.nombre
      });
    }
    form.setFieldsValue({ sucursalesIds: [] });
    setSucursalesIds([]);
  };

  const handleSubmit = async () => {
    if (!usuarioId) return;

    try {
      const values = await form.validateFields();
      setLoading(true);
      const {
        confirmar_contrasena,
        cambiar_contrasena,
        empresaNombre,
        ...dataToSubmit
      } = values;

      if (!cambiar_contrasena) {
        delete dataToSubmit.contrasena;
        delete dataToSubmit.contrasena_actual;
      } else {
        if (!dataToSubmit.contrasena_actual) {
          message.error('Debe ingresar su contraseña actual para cambiarla');
          setLoading(false);
          return;
        }
      }

      if (dataToSubmit.empresaId) {
        dataToSubmit.empresaId = Number(dataToSubmit.empresaId);
      }

      dataToSubmit.sucursalesIds = sucursalesIds;

      const cleanData: UsuarioDto = {
        nombre: dataToSubmit.nombre || '',
        empresaId: dataToSubmit.empresaId || 0,
        sucursalesIds: dataToSubmit.sucursalesIds || [],
        contrasena: dataToSubmit.contrasena || '',
        email: dataToSubmit.email || '',
        ...Object.fromEntries(
          Object.entries(dataToSubmit).filter(([_, v]) => v !== undefined && v !== null)
        )
      };

      const result = await dispatch(updateUsuario(usuarioId.toString(), cleanData));

      if (result && result.respuesta) {
        message.success('Usuario actualizado exitosamente');
        onClose(true);
      } else {
        message.error('Error al actualizar el usuario');
      }
    } catch (error: unknown) {
      console.error('Error al actualizar el usuario:', error);
      if (error && typeof error === 'object' && 'message' in error &&
        typeof error.message === 'string') {
        message.error(error.message);
      } else {
        message.error('Error al actualizar el usuario');
      }
    } finally {
      setLoading(false);
    }
  };
  const handleCancel = () => {
    onClose();
  };

  return (
    <Modal
      title="Editar Usuario"
      open={visible}
      width={700}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancelar
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleSubmit}
          disabled={!usuarioActual}
        >
          Guardar Cambios
        </Button>,
      ]}
    >
      {loadingData || !usuarioActual ? (
        <div className="flex justify-center items-center py-8">
          <Spin tip="Cargando información del usuario..." />
        </div>
      ) : (
        <Form
          form={form}
          layout="vertical"
          name="form_editar_usuario"
        >
          <div className='font-bold mt-5 mb-2'>Información Personal</div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item
              name="nombre"
              label="Nombre"
              rules={[
                {
                  required: true,
                  message: 'Por favor ingrese el nombre',
                },
              ]}
            >
              <Input placeholder="Ej. Juan" />
            </Form.Item>

            <Form.Item
              name="apellidos"
              label="Apellidos"
              rules={[
                {
                  required: true,
                  message: 'Por favor ingrese los apellidos',
                },
              ]}
            >
              <Input placeholder="Ej. Pérez García" />
            </Form.Item>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item
              name="telefono"
              label="Teléfono"
            >
              <Input placeholder="Ej. +51987654321" />
            </Form.Item>

            <Form.Item
              name="pais"
              label="País"
            >
              <Input placeholder="Ej. Perú" />
            </Form.Item>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item
              name="imagen_perfil"
              label="URL de Imagen de Perfil"
              rules={[
                {
                  type: 'url',
                  message: 'Por favor ingrese una URL válida',
                },
              ]}
            >
              <Input placeholder="https://ejemplo.com/imagen.jpg" />
            </Form.Item>

            <Form.Item
              name="timezone"
              label="Zona Horaria"
            >
              <Input placeholder="Ej. America/Lima" />
            </Form.Item>
          </div>

          <div className='font-bold mt-5 mb-2'>Información de Cuenta</div>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              {
                required: true,
                type: 'email',
                message: 'Por favor ingrese un email válido',
              },
            ]}
          >
            <Input placeholder="Ej. usuario@ejemplo.com" />
          </Form.Item>

          <Form.Item
            name="cambiar_contrasena"
            label="Cambiar Contraseña"
            valuePropName="checked"
          >
            <Switch
              checkedChildren="Sí"
              unCheckedChildren="No"
            />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.cambiar_contrasena !== currentValues.cambiar_contrasena}
          >
            {({ getFieldValue }) =>
              getFieldValue('cambiar_contrasena') ? (
                <div className="border p-4 rounded mb-4 bg-gray-50">
                  <p className="text-blue-600 mb-2">
                    Para cambiar la contraseña, primero debe verificar la contraseña actual
                  </p>

                  <Form.Item
                    name="contrasena_actual"
                    label="Contraseña Actual"
                    rules={[
                      {
                        required: true,
                        message: 'Por favor ingrese su contraseña actual',
                      }
                    ]}
                    hasFeedback
                  >
                    <Input.Password placeholder="Ingrese su contraseña actual" />
                  </Form.Item>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Form.Item
                      name="contrasena"
                      label="Nueva Contraseña"
                      rules={[
                        {
                          required: true,
                          message: 'Por favor ingrese una nueva contraseña',
                        },
                        {
                          min: 6,
                          message: 'La contraseña debe tener al menos 6 caracteres',
                        },
                      ]}
                      hasFeedback
                    >
                      <Input.Password placeholder="Ingrese una nueva contraseña" />
                    </Form.Item>
                    <Form.Item
                      name="confirmar_contrasena"
                      label="Confirmar Nueva Contraseña"
                      dependencies={['contrasena']}
                      hasFeedback
                      rules={[
                        {
                          required: true,
                          message: 'Por favor confirme su nueva contraseña',
                        },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (!value || getFieldValue('contrasena') === value) {
                              return Promise.resolve();
                            }
                            return Promise.reject(new Error('Las contraseñas no coinciden'));
                          },
                        }),
                      ]}
                    >
                      <Input.Password placeholder="Confirme su nueva contraseña" />
                    </Form.Item>
                  </div>
                </div>
              ) : null
            }
          </Form.Item>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item
              name="tipoUsuarioId"
              label="Tipo de Usuario"
              rules={[
                {
                  required: true,
                  message: 'Por favor seleccione un tipo de usuario',
                },
              ]}
            >
              <Select placeholder="Seleccione un tipo de usuario">
                {tiposUsuarios && tiposUsuarios.map((tipo: any) => (
                  <Option key={tipo.id} value={tipo.id}>
                    {tipo.tipo_usuario}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="empresaId"
              label="Empresa"
              rules={[
                {
                  required: true,
                  message: 'Por favor seleccione una empresa',
                },
              ]}
            >
              <EmpresasSelect
                onChange={handleEmpresaChange}
                value={selectedEmpresaId}
                placeholder="Seleccione una empresa"
              />
            </Form.Item>
          </div>

          <Form.Item
            name="sucursalesIds"
            label="Sucursales"
            rules={[
              {
                required: true,
                message: 'Por favor seleccione al menos una sucursal',
              },
            ]}
          >
            <Select
              mode="multiple"
              placeholder="Seleccione las sucursales"
              optionFilterProp="children"
              disabled={!selectedEmpresaId}
              onChange={(values) => {
                const numericValues = values.map(val => Number(val));
                setSucursalesIds(numericValues);
                form.setFieldsValue({ sucursalesIds: numericValues });
              }}
              value={sucursalesIds}
            >
              {sucursales && sucursales.map((sucursal: any) => (
                <Option
                  key={sucursal.id || sucursal.id_sucursal}
                  value={sucursal.id || sucursal.id_sucursal}
                >
                  {sucursal.nombre}
                </Option>
              ))}
            </Select>
            {sucursalesIds.length === 0 && (
              <div className="text-red-500 text-sm mt-1">
                No hay sucursales seleccionadas
              </div>
            )}
            {sucursalesIds.length > 0 && (
              <div className="mt-2 p-2 border rounded">
                <div className="font-medium mb-1">Sucursales seleccionadas:</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {sucursalesIds.map(id => {
                    const sucursal = sucursales.find(s => (s.id || s.id_sucursal) === id);
                    return (
                      <div key={id} className="flex justify-between items-center bg-gray-100 p-2 rounded">
                        <span>{sucursal ? sucursal.nombre : `Sucursal ID: ${id}`}</span>
                        <Button
                          type="text"
                          danger
                          size="small"
                          onClick={() => {
                            const newSucursalesIds = sucursalesIds.filter(sId => sId !== id);
                            setSucursalesIds(newSucursalesIds);
                            form.setFieldsValue({ sucursalesIds: newSucursalesIds });
                          }}
                        >
                          X
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </Form.Item>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item
              name="estado"
              label="Estado"
              valuePropName="checked"
            >
              <Switch
                checkedChildren="Activo"
                unCheckedChildren="Inactivo"
              />
            </Form.Item>
          </div>
        </Form>
      )}
    </Modal>
  );
};

export default EditarUsuarioModal;